import { FIELD_TYPES } from 'utility/constants';
import {
  getOptionValue,
  namedToOption,
  teammembersMapper,
  namedToOptionWithCode,
  relatedfieldIdToOption,
  getObjectToModifyDeleted,
  relatedfieldIdToOptionDeleted,
  displayNameToOption,
  displayNameToOptionError,
  variableToOptionDeleted,
} from 'services/helpers';

import UUID from 'utility/UUID';

import {
  fieldValueMappingsErrorsForFlow,
  removeDeletedOptionsInFieldValueMappings,
} from '__pages/AutomationEngine/dialogs/ActionWizard/subsections/CommonComponents/valueMappingsCommon';
import { fieldToModifyfieldType, namedToLabel } from './common';
import {
  FieldResolutionOptions,
  MreChangeTypeOptions,
  fieldResolutionOptionsConst,
  recordSourceOptionsConst,
} from './ts-common';
import { newFieldValueMappings } from './ts-common';
import { mreChangeTypeOptionsConst } from './ts-common';

import {
  checkNestedDeleted,
  checkDeleted,
  displayNameDescriptor,
  descriptionDescriptor,
  _checkListDeleted,
} from '__pages/AutomationEngine/helpers';
import {
  mapToFlowFieldToClear,
  mapToApiFieldToClear,
} from 'pages/AutomationEngine/dialogs/fieldMapper';
import {
  ApiFieldToModifyOption,
  Field,
  FieldValueMappings,
  GenericOption,
  KizenFieldType,
} from 'pages/AutomationEngine/types';
import { FieldsToModify, ModifyRelatedEntriesValues } from './types';
import { InitialState } from './hooks/types';
import {
  AddErrorMessage,
  MessageDictionary,
} from '__pages/AutomationEngine/messageDictionary';

type WithId = { id: string };
type WithValue = { value: string };

export const initialState = (
  fieldToModify: FieldsToModify | null = null,
  isGlobal: boolean
): InitialState => ({
  type: 'modify_related_entities',
  recordSource: isGlobal
    ? recordSourceOptionsConst.AUTOMATION_VARIABLE
    : recordSourceOptionsConst.RELATED_OBJECT_FIELD,
  objectToModify: null,
  automationTargetRelationshipFields: [],
  fieldsToModify: [fieldToModify],
  recordSourceVariables: [],
});

const isNaInt = (value: any) => isNaN(parseInt(value));

const parseNumberOrNull = (value: any) =>
  isNaInt(value) ? null : parseFloat(value);

const parseIntOrNull = (value: any) =>
  isNaInt(value) ? null : parseInt(value);

const setSpecificValueForFlow = (
  field: ApiFieldToModifyOption,
  value: any,
  specificFieldValue: any
) => {
  if (!specificFieldValue || specificFieldValue?.deleted) {
    return null;
  }

  switch (field?.fieldType) {
    case FIELD_TYPES.DateTime.type:
      return { value };
    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
    case FIELD_TYPES.YesNoMaybe.type:
    case FIELD_TYPES.YesNo.type:
      return namedToOption(specificFieldValue);

    case FIELD_TYPES.Checkboxes.type:
      return {
        value: (specificFieldValue || []).map((opt: any) => {
          return namedToOptionWithCode(opt);
        }),
      };

    case FIELD_TYPES.TeamSelector.type:
      return teammembersMapper(specificFieldValue);

    case FIELD_TYPES.Rating.type:
      return { value: String(specificFieldValue.value) };

    case FIELD_TYPES.DynamicTags.type: {
      return {
        value: {
          tagsToAdd: specificFieldValue.tagsToAdd.map(namedToLabel),
          tagsToRemove: specificFieldValue.tagsToRemove.map(namedToLabel),
        },
      };
    }

    case FIELD_TYPES.Files.type:
      return { value: specificFieldValue };

    case FIELD_TYPES.Relationship.type: {
      return {
        value: Array.isArray(specificFieldValue)
          ? specificFieldValue.map(namedToLabel)
          : namedToLabel(specificFieldValue),
      };
    }

    default:
      return specificFieldValue;
  }
};

const fieldResolutionForFlow = (fieldResolution: FieldResolutionOptions) => {
  // these will get translated if the user opens the edit wizard
  switch (fieldResolution) {
    case fieldResolutionOptionsConst.ONLY_ADD_OPTIONS:
      return {
        value: fieldResolution,
        label: 'Only Add Options',
      };

    case fieldResolutionOptionsConst.ONLY_UPDATE_BLANK:
      return {
        value: fieldResolution,
        label: 'Only Update Blank',
      };

    default:
      return {
        value: fieldResolutionOptionsConst.OVERWRITE,
        label: 'Overwrite Field',
      };
  }
};

const fieldValueMappingsForFlow = (
  fieldValueMappings: Array<Omit<FieldValueMappings, 'key'>>
) => {
  if (fieldValueMappings) {
    const mapped = fieldValueMappings
      .map(({ sourceValues, targetValue, targetValues, ...rest }) => {
        return {
          key: UUID.generate(),
          sourceValues: (sourceValues || []).map((sourceValue) =>
            typeof sourceValue === 'object' && sourceValue?.value
              ? { ...sourceValue, value: String(sourceValue?.value) }
              : typeof sourceValue === 'object' && sourceValue?.id
                ? { ...sourceValue, value: String(sourceValue?.id) }
                : sourceValue !== null
                  ? String(sourceValue)
                  : null
          ),
          targetValues: (targetValues || []).map((targetValue) => {
            return typeof targetValue === 'object' && targetValue?.value
              ? { ...targetValue, value: String(targetValue?.value) }
              : typeof targetValue === 'object' && targetValue?.id
                ? { ...targetValue, value: String(targetValue?.id) }
                : targetValue !== null
                  ? String(targetValue)
                  : null;
          }),
          ...rest,
        };
      })
      .sort((a, b) => {
        return a.order - b.order;
      });
    return mapped;
  }

  return [newFieldValueMappings(0)];
};

const getValueTypeOption = (valueType: MreChangeTypeOptions) => {
  switch (valueType) {
    case mreChangeTypeOptionsConst.SPECIFIC_VALUE:
      return {
        value: mreChangeTypeOptionsConst.SPECIFIC_VALUE,
        label: 'Specific Value', // will get translated when needed
      };
    case mreChangeTypeOptionsConst.RELATED_OBJECT_FIELD:
      return {
        value: mreChangeTypeOptionsConst.RELATED_OBJECT_FIELD,
        label: '{{entityName}} Field Value', // will get translated when needed
      };
    case mreChangeTypeOptionsConst.ENTITY_VARIABLE_FIELD:
      return {
        value: mreChangeTypeOptionsConst.ENTITY_VARIABLE_FIELD,
        label: 'Agentic Workflow Variable Record Field', // will get translated when needed
      };
    case mreChangeTypeOptionsConst.BLANK_VALUE:
      return {
        value: mreChangeTypeOptionsConst.BLANK_VALUE,
        label: 'Blank Value', // will get translated when needed
      };

    default:
      return {
        value: mreChangeTypeOptionsConst.VARIABLE,
        label: 'Agentic Workflow Variable', // will get translated when needed
      };
  }
};

type ApiFieldsToModify = Omit<
  FieldsToModify,
  | 'valueType'
  | 'fieldsToClear'
  | 'fieldResolution'
  | 'fieldToModify'
  | 'specificFieldValue'
  | 'fieldValueMappings'
> & {
  valueType: MreChangeTypeOptions;
  specificFieldValue: string | (GenericOption & WithId) | null;
  fieldsToClear: Array<ApiFieldToModifyOption>;
  fieldResolution: FieldResolutionOptions;
  fieldToModify: ApiFieldToModifyOption;
  fieldValueMappings: Array<Omit<FieldValueMappings, 'key'>>;
};

type ApiModifyRelatedEntriesValues = Omit<
  ModifyRelatedEntriesValues,
  'fieldsToModify'
> & { fieldsToModify: Array<ApiFieldsToModify> };

const forFlow = (
  apiObject: ApiModifyRelatedEntriesValues,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  try {
    const {
      id,
      objectToModify,
      fieldsToModify,
      recordSource,
      recordSourceVariables,
    } = apiObject;
    // filter if an array of fields is passed in
    const automationTargetRelationshipFields = Array.isArray(
      apiObject.automationTargetRelationshipFields
    )
      ? apiObject.automationTargetRelationshipFields.filter((f) => !f.deleted)
      : apiObject.automationTargetRelationshipFields;

    checkDeleted(
      messageDictionary,
      addErrorMessage,
      objectToModify,
      descriptionDescriptor
    );

    checkNestedDeleted(
      messageDictionary,
      addErrorMessage,
      fieldsToModify,
      'contextEntityField',
      displayNameDescriptor
    );

    if (fieldsToModify?.length > 0) {
      checkNestedDeleted(
        messageDictionary,
        addErrorMessage,
        fieldsToModify,
        'fieldToModify',
        displayNameDescriptor
      );
    } else {
      addErrorMessage(messageDictionary.automationFailedToLoad);
    }

    if (recordSource === recordSourceOptionsConst.RELATED_OBJECT_FIELD) {
      _checkListDeleted({
        messageDictionary,
        addErrorMessage,
        parentObject: automationTargetRelationshipFields,
        descriptorCallback: displayNameDescriptor,
        itemName: messageDictionary.targetRelationshipFields,
        objectPath: 'automationTargetRelationshipFields',
        objectErrorTrans: displayNameToOptionError,
      });
    }

    const firstFieldToModify = fieldsToModify?.[0];

    if (firstFieldToModify?.updateMode === 'clear_fields') {
      _checkListDeleted({
        messageDictionary,
        addErrorMessage,
        parentObject: firstFieldToModify?.fieldsToClear,
        descriptorCallback: displayNameDescriptor,
        itemName: '!!!boom!!@', //messageDictionary.targetRelationshipFields,
        objectPath: 'fieldsToClear',
        objectErrorTrans: displayNameToOptionError,
      });
    }

    const flow = {
      id,
      objectToModify: getObjectToModifyDeleted(objectToModify),
      automationTargetRelationshipFields: (
        automationTargetRelationshipFields || []
      ).map((f) => relatedfieldIdToOption(f)),
      recordSource:
        recordSource ?? recordSourceOptionsConst.RELATED_OBJECT_FIELD,
      recordSourceVariables: (recordSourceVariables || []).map((name) => ({
        label: name,
        value: name,
        deleted: false,
      })),
      fieldsToModify: fieldsToModify.map(
        ({
          fieldToModify,
          specificFieldValue,
          contextEntityField,
          valueType,
          fieldResolution,
          fieldValueMappings: _fieldValueMappings,
          variable,
          updateMode,
          fieldsToClear,
        }) => {
          let fieldValueMappings;
          let initialValidation = {};
          let fieldValueMappingsErrors = null;
          const fvm = fieldValueMappingsForFlow(_fieldValueMappings);

          if (valueType === mreChangeTypeOptionsConst.SPECIFIC_VALUE) {
            fieldValueMappings = fvm;
          } else {
            ({ fieldValueMappings, initialValidation } =
              removeDeletedOptionsInFieldValueMappings({
                fieldToModify,
                sourceOptions: contextEntityField?.options,
                targetOptions: fieldToModify?.options,
                addErrorMessage,
                messageDictionary,
                fieldValueMappings: fvm,
              }));
            fieldValueMappingsErrors =
              fieldValueMappingsErrorsForFlow(fieldValueMappings);
          }

          return {
            key: UUID.generate(),
            fieldToModify: fieldToModify
              ? relatedfieldIdToOptionDeleted(fieldToModify)
              : undefined,
            specificFieldValue: fieldToModify
              ? setSpecificValueForFlow(
                  fieldToModify,
                  typeof specificFieldValue === 'object'
                    ? specificFieldValue?.value || specificFieldValue?.id
                    : specificFieldValue,
                  specificFieldValue
                )
              : undefined,
            valueType: getValueTypeOption(valueType),

            fieldResolution: fieldResolutionForFlow(fieldResolution),
            ...(contextEntityField && !contextEntityField?.deleted
              ? {
                  contextEntityField: {
                    ...contextEntityField, // we used option firld type etc now
                    ...displayNameToOption(contextEntityField),
                  },
                }
              : null),
            fieldValueMappings,
            fieldValueMappingsErrors: fieldValueMappingsErrors,
            fieldValueMappingsValidation: fieldValueMappingsErrors,
            duplicateError: false,
            variable: variable ? variableToOptionDeleted(variable) : null,
            updateMode,
            fieldsToClear: Array.isArray(fieldsToClear)
              ? (fieldsToClear || []).map(mapToFlowFieldToClear)
              : undefined,
            ...initialValidation,
          };
        }
      ),
      fixMe: true,
      isChangeActionMutable: false,
    };
    return flow;
  } catch (error) {
    addErrorMessage(messageDictionary.automationFailedToLoad);
    return initialState;
  }
};

const getValueForApi = (field: Field, specificFieldValue: any) => {
  if (!specificFieldValue) {
    return null;
  }
  const { value } = specificFieldValue;

  switch (field.fieldType) {
    case FIELD_TYPES.Text.type:
    case FIELD_TYPES.LongText.type:
      return value || '';

    case FIELD_TYPES.Decimal.type:
    case FIELD_TYPES.Money.type:
    case FIELD_TYPES.Integer.type:
      return parseNumberOrNull(value);

    case FIELD_TYPES.DateTime.type:
      return value || null;

    case FIELD_TYPES.Dropdown.type:
    case FIELD_TYPES.Radio.type:
    case FIELD_TYPES.Status.type:
    case FIELD_TYPES.TeamSelector.type:
      return value?.value ?? value;

    case FIELD_TYPES.Rating.type:
      return parseIntOrNull(value);

    case FIELD_TYPES.Checkboxes.type:
      return (value || []).map(({ value: id }: WithValue) => id);

    case FIELD_TYPES.DynamicTags.type: {
      return {
        tagsToAdd: value?.tagsToAdd?.map(({ id }: WithId) => id) || [],
        tagsToRemove: value?.tagsToRemove?.map(({ id }: WithId) => id) || [],
      };
    }

    case FIELD_TYPES.Files.type:
      return (value || []).map(({ id }: WithId) => id);

    case FIELD_TYPES.Relationship.type:
      return Array.isArray(value)
        ? value.map(({ id }: WithId) => id)
        : value?.id;

    default:
      return value || null;
  }
};

const getfieldValueMapping = (value: any, fieldType: KizenFieldType) =>
  fieldType === FIELD_TYPES.Rating.type ? parseIntOrNull(value) : value;

const getfieldValueMappings = (
  fieldValueMappings: Array<FieldValueMappings>,
  fieldType: KizenFieldType
) => {
  const mapped = fieldValueMappings.map(
    ({ order, sourceValues, targetValues }) => ({
      order,
      sourceValues: (sourceValues || []).map((sourceValue: any) =>
        getfieldValueMapping(
          typeof sourceValue === 'object' ? sourceValue?.value : sourceValue,
          fieldType
        )
      ),
      targetValues: (targetValues || [])
        .filter(Boolean)
        .map((targetValue) =>
          getfieldValueMapping(
            typeof targetValue === 'object' ? targetValue?.value : targetValue,
            fieldType
          )
        ),
    })
  );
  return mapped;
};

type ForApi = {
  details: ModifyRelatedEntriesValues;
};

const forApi = (step: ForApi) => {
  const details = { ...step.details };
  const {
    objectToModify,
    automationTargetRelationshipFields,
    fieldsToModify,
    recordSource,
    recordSourceVariables,
  } = details;
  const api = {
    details: {
      objectToModify: getOptionValue(objectToModify),
      automationTargetRelationshipFields:
        automationTargetRelationshipFields.map((f: any) => getOptionValue(f)),
      recordSource,
      recordSourceVariables: recordSourceVariables.map(({ label }) => label),

      //
      fieldsToModify: fieldsToModify.map(
        ({
          fieldToModify,
          specificFieldValue,
          contextEntityField,
          valueType,
          fieldResolution,
          fieldValueMappings,
          variable,
          updateMode,
          fieldsToClear,
        }) => {
          return {
            fieldToModify: fieldToModify ? getOptionValue(fieldToModify) : null,
            specificFieldValue: getValueForApi(
              fieldToModify?.fieldData!,
              specificFieldValue
            ),
            valueType: getOptionValue(valueType),
            ...(contextEntityField && {
              contextEntityField: getOptionValue(contextEntityField),
            }),
            fieldResolution: getOptionValue(fieldResolution),
            fieldValueMappings: getfieldValueMappings(
              fieldValueMappings,
              fieldToModifyfieldType(fieldToModify)
            ),
            variable: variable?.value,
            updateMode,
            fieldsToClear: Array.isArray(fieldsToClear)
              ? fieldsToClear.map(mapToApiFieldToClear)
              : undefined,
          };
        }
      ),
    },
  };

  return api;
};

export const modifyRelatedEntitiesDTO = {
  forApi,
  forFlow,
};
